import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import MaskedInput from 'react-text-mask';
import toast, { Toaster } from 'react-hot-toast';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { DarkVeil } from '../components';


export default function Register() {
    // const { login } = useAuth(); // Auto-login disabled after registration
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState(location.state?.email || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState(location.state?.firstName || '');
    const [lastName, setLastName] = useState(location.state?.lastName || '');
    const [loading, setLoading] = useState(false);
    const [referralCode, setReferralCode] = useState('');
    const [phone, setPhone] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [emailError, setEmailError] = useState('');
    const { t } = useTranslation();

    // Banner'dan gelen e-postayı otomatik doldur
    useEffect(() => {
        const prefilledEmail = localStorage.getItem('prefilledEmail');
        if (prefilledEmail) {
            setEmail(prefilledEmail);
            localStorage.removeItem('prefilledEmail');
        }
    }, []);

    // UTM parametrelerini URL'den yakala ve localStorage'a kaydet
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const utmSource = params.get('utm_source');
        const utmMedium = params.get('utm_medium');
        const utmCampaign = params.get('utm_campaign');
        if (utmSource) localStorage.setItem('utm_source', utmSource);
        if (utmMedium) localStorage.setItem('utm_medium', utmMedium);
        if (utmCampaign) localStorage.setItem('utm_campaign', utmCampaign);
    }, []);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        // Email required check
        if (!email || email.trim() === '') {
            setEmailError(t('register.emailRequired'));
            return;
        }

        // Email validation
        if (!validateEmail(email)) {
            setEmailError(t('register.invalidEmail'));
            return;
        }
        setEmailError('');

        // Password required check
        if (!password || !confirmPassword) return;

        // Password match validation
        if (password !== confirmPassword) {
            setPasswordError(t('register.passwordMismatch'));
            return;
        }
        setPasswordError('');

        if (!firstName || !lastName || !phone) return;

        setLoading(true);
        try {
            const userData = {
                FirstName: firstName,
                LastName: lastName,
                Email: email,
                Phone: phone.replace(/\D/g, ''),
                Password: password,
                IdentityNo: "",
                dateOfBirth: new Date().toISOString().split('T')[0],
                referredBy: '',
                utmSource: localStorage.getItem('utm_source') || '',
                utmMedium: localStorage.getItem('utm_medium') || '',
                utmCampaign: localStorage.getItem('utm_campaign') || ''
            };

            const result = await api.registerUser(userData);
            console.log('Registration successful:', result);
            toast.success(t('register.toastSuccess'));
            navigate('/login');
        } catch (err) {
            console.error('Registration failed:', err);
            let errorMessage = err.message || t('register.toastError');

            // Check if email already exists
            if (err.message && (err.message.includes('email') || err.message.includes('Email') || err.message.includes('already exists'))) {
                errorMessage = t('register.emailAlreadyExists');
                setEmailError(errorMessage);
            }

            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="new-login-page">
            <div className="new-login-container">
                <div className="new-login-left">
                    <div className="new-login-left-animation">
                        <DarkVeil
                            colors={["#008cffff", "#9b00afff", "#00ffb3ff"]}
                            rotation={0}
                            speed={0.2}
                            scale={1}
                            frequency={1}
                            warpStrength={1}
                            mouseInfluence={1}
                            parallax={0.5}
                            noise={0.1}
                            transparent
                            autoRotate={0}
                        />
                    </div>
                    <div className="new-login-left-content">
                        <h2 className="new-login-left-title">
                            {t('register.newLeftTitle')}
                        </h2>
                        <p className="new-login-left-subtitle">
                            {t('register.newLeftSubtitle')}
                        </p>
                        {/* <div className="new-login-social">
                            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                                <FaFacebook size={20} />
                                Facebook
                            </a>
                            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                                <FaInstagram size={20} />
                                Instagram
                            </a>
                            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                                <FaLinkedin size={20} />
                                LinkedIn
                            </a>
                        </div> */}
                    </div>
                </div>
                <div className="new-login-right">
                    {/* <div className="new-login-right-header">
                        <Link to="/login" className="new-login-signup-btn">
                            {t('register.loginLink')}
                        </Link>
                    </div> */}
                    <div className="new-login-form-container">
                        <div className="new-login-logo">
                            <img
                                src="/assets/images/nordex_register_image.png"
                                alt="Nordex Register"
                                className="login-brand-image"
                            />
                        </div>
                        <form className="new-login-form" onSubmit={onSubmit}>
                            <div className="new-login-input-row">
                                <div className="new-login-input-group">
                                    <label htmlFor="firstName">{t('register.firstNameLabel')}</label>
                                    <input
                                        id="firstName"
                                        type="text"
                                        placeholder={t('register.firstNamePlaceholder')}
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="new-login-input-group">
                                    <label htmlFor="lastName">{t('register.lastNameLabel')}</label>
                                    <input
                                        id="lastName"
                                        type="text"
                                        placeholder={t('register.lastNamePlaceholder')}
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="new-login-input-group">
                                <label htmlFor="email">{t('register.emailLabel')}</label>
                                <input
                                    id="email"
                                    type="email"
                                    placeholder={t('register.emailPlaceholder')}
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value.replace(/\s/g, ''));
                                        setEmailError('');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === ' ') {
                                            e.preventDefault();
                                        }
                                    }}
                                    style={emailError ? { borderColor: '#EF4444' } : {}}
                                />
                                {emailError && (
                                    <span style={{ color: '#EF4444', fontSize: '13px', marginTop: '4px' }}>
                                        {emailError}
                                    </span>
                                )}
                            </div>
                            <div className="new-login-input-group">
                                <label htmlFor="phone">{t('register.phoneLabel')}</label>
                                <MaskedInput
                                    mask={['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/]}
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder={t('register.phonePlaceholder')}
                                    guide={false}
                                    keepCharPositions={false}
                                    render={(ref, props) => (
                                        <input
                                            {...props}
                                            ref={ref}
                                            id="phone"
                                            type="tel"
                                            placeholder='(5xx) xxx xxxx'
                                            required
                                        />
                                    )}
                                />
                            </div>
                            <div className="new-login-input-group">
                                <label htmlFor="password">{t('register.passwordLabel')}</label>
                                <div className="new-login-password-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder={t('register.passwordPlaceholder')}
                                    />
                                    <button
                                        type="button"
                                        className="new-login-password-toggle"
                                        onClick={() => setShowPassword((s) => !s)}
                                    >
                                        {showPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                                    </button>
                                </div>
                            </div>
                            <div className="new-login-input-group">
                                <label htmlFor="confirmPassword">{t('register.confirmPasswordLabel')}</label>
                                <div className="new-login-password-wrapper">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        id="confirmPassword"
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            setConfirmPassword(e.target.value);
                                            setPasswordError('');
                                        }}
                                        placeholder={t('register.confirmPasswordPlaceholder')}
                                        style={passwordError ? { borderColor: '#EF4444' } : {}}
                                    />
                                    <button
                                        type="button"
                                        className="new-login-password-toggle"
                                        onClick={() => setShowConfirmPassword((s) => !s)}
                                    >
                                        {showConfirmPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                                    </button>
                                </div>
                                {passwordError && (
                                    <span style={{ color: '#EF4444', fontSize: '13px', marginTop: '4px' }}>
                                        {passwordError}
                                    </span>
                                )}
                            </div>
                            <div className="new-login-input-group">
                                <label htmlFor="referralCode">{t('register.referralLabel')} <span className="optional-label">(Opsiyonel)</span></label>
                                <input
                                    id="referralCode"
                                    type="text"
                                    placeholder={t('register.referralPlaceholder')}
                                    value={referralCode}
                                    onChange={(e) => setReferralCode(e.target.value)}
                                />
                            </div>
                            <button
                                type="submit"
                                className="new-login-submit-btn"
                                disabled={loading}
                            >
                                {loading ? t('register.submitting') : t('register.submit')}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            <Toaster
                    position="top-center"
                    toastOptions={{
                        duration: 4000,
                        style: {
                            background: '#363636',
                            color: '#fff',
                        },
                        success: {
                            duration: 3000,
                            style: {
                                background: '#10B981',
                                color: '#fff',
                            },
                        },
                        error: {
                            duration: 5000,
                            style: {
                                background: '#EF4444',
                                color: '#fff',
                            },
                        },
                    }}
                />
            </div>
    );
}


